import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Search } from "lucide-react";
import { fetcher, http, loadPublicConfig } from "@/lib/api";
import { formatINR } from "@/lib/utils";

const STEPS = [
  "Product",
  "Plan",
  "Size",
  "Container",
  "Details",
  "Review",
];

export default function OrderSample() {
  const { slug: paramSlug } = useParams();
  const [params] = useSearchParams();
  const initialSlug = paramSlug || params.get("product") || null;
  const navigate = useNavigate();

  const { data: products } = useSWR("/products", fetcher);
  const { data: categories } = useSWR("/categories", fetcher);
  const { data: opts } = useSWR("/packaging-options", fetcher);

  const [config, setConfig] = useState({
    productSlug: initialSlug || "",
    planName: "",
    sizeIdx: 0,
    containerIdx: 0,
    labelFinish: "matte",
    quantity: 1,
    logoUrl: "",
    name: "",
    email: "",
    phone: "",
    company: "",
    country: "India",
    notes: "",
  });
  const [step, setStep] = useState(initialSlug ? 1 : 0);
  const [productFilter, setProductFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [pubCfg, setPubCfg] = useState(null);

  useEffect(() => {
    loadPublicConfig().then(setPubCfg).catch(() => {});
  }, []);

  const selectedProduct = useMemo(
    () => (products || []).find((p) => p.slug === config.productSlug) || null,
    [products, config.productSlug],
  );

  const selectedPlan = useMemo(() => {
    if (!selectedProduct?.plans?.length) return null;
    return (
      selectedProduct.plans.find((pl) => pl.name === config.planName) ||
      selectedProduct.plans[0]
    );
  }, [selectedProduct, config.planName]);

  const selectedSku = useMemo(() => {
    const list = selectedProduct?.sku || [];
    return list[config.sizeIdx] || list[0] || null;
  }, [selectedProduct, config.sizeIdx]);

  const selectedContainer = useMemo(() => {
    const list = selectedSku?.containers || [];
    return list[config.containerIdx] || list[0] || null;
  }, [selectedSku, config.containerIdx]);

  const subtotal =
    (selectedPlan?.price || 0) + (selectedContainer?.price || 0);
  const gstRate = selectedProduct?.gst || 18;
  const unitPrice = Math.round(subtotal * (1 + gstRate / 100));
  const total = unitPrice * Math.max(1, config.quantity);

  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const canProceed = () => {
    if (step === 0) return !!config.productSlug;
    if (step === 1) return !!selectedPlan;
    if (step === 2) return !!selectedSku;
    if (step === 3) return !!selectedContainer;
    if (step === 4)
      return config.name && config.email && config.phone && config.quantity >= 1;
    return true;
  };

  const next = () => {
    if (!canProceed()) return toast.error("Please complete this step.");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  // Auto-set default plan/container on product change
  useEffect(() => {
    if (!selectedProduct) return;
    if (!selectedProduct.plans?.some((p) => p.name === config.planName)) {
      set("planName", selectedProduct.plans?.[0]?.name || "");
    }
    if ((selectedProduct.sku?.length || 0) <= config.sizeIdx) {
      set("sizeIdx", 0);
    }
  }, [selectedProduct]);

  useEffect(() => {
    set("containerIdx", 0);
  }, [config.sizeIdx]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: order } = await http.post("/orders/create", {
        name: config.name,
        email: config.email,
        phone: config.phone,
        company: config.company,
        country: config.country,
        amount_inr: total,
        config: {
          product_slug: config.productSlug,
          packaging: `${selectedContainer?.image || ""}`.slice(0, 200) || "container",
          size: selectedSku?.size || "",
          quantity: config.quantity,
          label_finish: config.labelFinish,
          logo_url: config.logoUrl,
          notes: `Plan: ${selectedPlan?.name} @ ₹${selectedPlan?.price} | Container: ₹${selectedContainer?.price} | GST ${gstRate}% | ${config.notes || ""}`,
        },
      });

      if (order.test_mode) {
        await http.post(`/orders/test-confirm?order_id=${order.order_id}`);
        toast.success("Sample request recorded (test mode). Our team will call you.");
        navigate(`/order-sample?confirmed=${order.order_id}`);
        setStep(0);
        return;
      }

      const options = {
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: "INR",
        order_id: order.razorpay_order_id,
        name: "Fresio Herbals",
        description: `Sample: ${selectedProduct?.name || "Fresio Product"}`,
        prefill: {
          name: config.name,
          email: config.email,
          contact: config.phone,
        },
        theme: { color: "#1D3A2F" },
        handler: async (res) => {
          try {
            await http.post("/orders/verify", {
              order_id: order.order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful — order confirmed!");
            navigate(`/order-sample?confirmed=${order.order_id}`);
          } catch {
            toast.error("Payment recorded but verification failed.");
          }
        },
      };

      if (!window.Razorpay) {
        await loadRazorpayScript();
      }
      new window.Razorpay(options).open();
    } catch (e) {
      console.error(e);
      toast.error("Could not create your order. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-5">Order Sample</div>
            <h1 className="font-editorial text-5xl md:text-7xl text-[#1D3A2F] leading-[0.95]">
              Design your sample.
              <br />
              <em className="gold-text">Delivered in 72 hours.</em>
            </h1>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="text-[#222222]/70 text-lg">
              Product · plan · size · container · label finish. Live pricing
              from our formulation lab and packaging library.
            </p>
          </div>
        </div>

        {/* Stepper */}
        <div className="mb-14 flex flex-wrap gap-2">
          {STEPS.map((label, i) => (
            <button
              key={label}
              onClick={() => i < step && setStep(i)}
              data-testid={`step-nav-${i}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs tracking-[0.16em] uppercase transition ${
                i === step
                  ? "bg-[#1D3A2F] text-[#FDFBF7]"
                  : i < step
                    ? "bg-[#556B57]/15 text-[#1D3A2F]"
                    : "bg-[#FDFBF7] text-[#222222]/40 border border-[#D7D4CF]/60"
              }`}
            >
              <span>{String(i + 1).padStart(2, "0")}</span>
              <span>{label}</span>
              {i < step && <Check className="w-3 h-3" />}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Main panel */}
          <div className="lg:col-span-8">
            <div className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-8 md:p-12 min-h-[440px]">
              {step === 0 && (
                <StepProduct
                  products={products || []}
                  categories={categories || []}
                  filter={productFilter}
                  setFilter={setProductFilter}
                  search={search}
                  setSearch={setSearch}
                  value={config.productSlug}
                  onChange={(v) => set("productSlug", v)}
                />
              )}
              {step === 1 && selectedProduct && (
                <StepPlan
                  plans={selectedProduct.plans || []}
                  value={selectedPlan?.name}
                  onChange={(v) => set("planName", v)}
                />
              )}
              {step === 2 && selectedProduct && (
                <StepSize
                  skus={selectedProduct.sku || []}
                  value={config.sizeIdx}
                  onChange={(v) => set("sizeIdx", v)}
                />
              )}
              {step === 3 && selectedSku && (
                <StepContainer
                  containers={selectedSku.containers || []}
                  size={selectedSku.size}
                  value={config.containerIdx}
                  onChange={(v) => set("containerIdx", v)}
                />
              )}
              {step === 4 && (
                <StepDetails
                  config={config}
                  onChange={set}
                  unitPrice={unitPrice}
                />
              )}
              {step === 5 && (
                <StepReview
                  product={selectedProduct}
                  plan={selectedPlan}
                  sku={selectedSku}
                  container={selectedContainer}
                  config={config}
                  opts={opts}
                  unitPrice={unitPrice}
                  total={total}
                  gstRate={gstRate}
                />
              )}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={back}
                disabled={step === 0}
                data-testid="order-back"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#1D3A2F]/25 text-[#1D3A2F] text-xs tracking-[0.18em] uppercase font-medium hover:border-[#1D3A2F] transition disabled:opacity-30"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  onClick={next}
                  data-testid="order-next"
                  className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-xs tracking-[0.18em] uppercase font-medium hover:bg-black transition"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="order-submit"
                  className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-xs tracking-[0.18em] uppercase font-medium hover:bg-black transition disabled:opacity-70"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {pubCfg?.razorpay_live ? "Pay & Confirm" : "Confirm Order"}{" "}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Preview panel */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] p-8">
              <div className="eyebrow text-[#FDFBF7]/60 mb-4">Live Preview</div>

              {/* Product image / container image side-by-side */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="aspect-square rounded-2xl overflow-hidden bg-[#FDFBF7]/5 border border-[#FDFBF7]/10 flex items-center justify-center">
                  {selectedProduct ? (
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-[10px] text-[#FDFBF7]/40 text-center px-2">
                      Choose a product
                    </div>
                  )}
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden bg-[#FDFBF7]/5 border border-[#FDFBF7]/10 flex items-center justify-center">
                  {selectedContainer?.image ? (
                    <img
                      src={selectedContainer.image}
                      alt="Container"
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <div className="text-[10px] text-[#FDFBF7]/40 text-center px-2">
                      Choose container
                    </div>
                  )}
                </div>
              </div>

              <h3 className="font-editorial text-2xl leading-tight">
                {selectedProduct?.name || "Your Product"}
              </h3>

              <ul className="mt-6 space-y-2 text-sm">
                <PreviewRow label="Plan" value={selectedPlan?.name} />
                <PreviewRow label="Size" value={selectedSku?.size} />
                <PreviewRow
                  label="Container"
                  value={
                    selectedContainer
                      ? `₹${selectedContainer.price}`
                      : "—"
                  }
                />
                <PreviewRow label="Quantity" value={config.quantity} />
              </ul>

              <div className="mt-6 pt-6 border-t border-[#FDFBF7]/10 space-y-1">
                <PriceRow
                  label="Formulation"
                  value={selectedPlan ? formatINR(selectedPlan.price) : "—"}
                />
                <PriceRow
                  label="Container"
                  value={
                    selectedContainer
                      ? formatINR(selectedContainer.price)
                      : "—"
                  }
                />
                <PriceRow
                  label={`GST @ ${gstRate}%`}
                  value={
                    subtotal
                      ? formatINR(Math.round(subtotal * (gstRate / 100)))
                      : "—"
                  }
                />
                <div className="flex items-end justify-between pt-3">
                  <span className="eyebrow text-[#FDFBF7]/60">Total</span>
                  <span
                    className="font-editorial text-3xl"
                    data-testid="preview-total"
                  >
                    {total ? formatINR(total) : "—"}
                  </span>
                </div>
              </div>
              {!pubCfg?.razorpay_live && (
                <p className="mt-4 text-[10px] tracking-[0.14em] uppercase text-[#FDFBF7]/40">
                  Test mode — order will be recorded, no card charged.
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <li className="flex items-center justify-between gap-4 border-b border-[#FDFBF7]/10 pb-2">
      <span className="text-[#FDFBF7]/50 text-xs tracking-[0.14em] uppercase">
        {label}
      </span>
      <span className="text-[#FDFBF7] text-right text-sm">
        {value || "—"}
      </span>
    </li>
  );
}

function PriceRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-[#FDFBF7]/50">{label}</span>
      <span className="text-[#FDFBF7]/85">{value}</span>
    </div>
  );
}

// ────────────── STEP 1 · Product ──────────────
function StepProduct({
  products,
  categories,
  filter,
  setFilter,
  search,
  setSearch,
  value,
  onChange,
}) {
  const filtered = products.filter((p) => {
    if (filter !== "all" && p.category !== filter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });
  return (
    <div>
      <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-6">
        Choose product ({products.length} available)
      </h3>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#556B57]" />
        <input
          data-testid="product-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search formulas — PDRN, retinal, ceramide…"
          className="w-full h-12 pl-11 pr-5 rounded-full bg-[#F7F4EE] border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#1D3A2F]"
        />
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <FilterChip
          active={filter === "all"}
          onClick={() => setFilter("all")}
          label={`All ${products.length}`}
        />
        {categories.map((c) => {
          const count = products.filter((p) => p.category === c.slug).length;
          if (!count) return null;
          return (
            <FilterChip
              key={c.slug}
              active={filter === c.slug}
              onClick={() => setFilter(c.slug)}
              label={`${c.name} ${count}`}
            />
          );
        })}
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[560px] overflow-y-auto pr-2">
        {filtered.map((p) => (
          <button
            key={p.slug}
            onClick={() => onChange(p.slug)}
            data-testid={`select-product-${p.slug}`}
            className={`text-left rounded-2xl overflow-hidden border transition ${
              value === p.slug
                ? "border-[#1D3A2F] ring-2 ring-[#1D3A2F]/40"
                : "border-[#D7D4CF]/60 hover:border-[#1D3A2F]/50"
            } bg-[#F7F4EE]`}
          >
            <div className="aspect-square overflow-hidden bg-white">
              {p.image ? (
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
            <div className="p-3">
              <div className="text-sm font-medium text-[#1D3A2F] line-clamp-2 min-h-[2.5em]">
                {p.name}
              </div>
              <div className="mt-1 flex items-center justify-between">
                <div className="text-[9px] text-[#556B57] tracking-[0.14em] uppercase">
                  {p.category.replace("-", " ")}
                </div>
                {p.price_inr > 0 && (
                  <div className="text-xs text-[#1D3A2F] font-medium">
                    from ₹{p.price_inr}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-[#556B57] text-sm">
            No products match your search.
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase transition ${
        active
          ? "bg-[#1D3A2F] text-[#FDFBF7]"
          : "bg-[#F7F4EE] text-[#1D3A2F] border border-[#D7D4CF]"
      }`}
    >
      {label}
    </button>
  );
}

// ────────────── STEP 2 · Plan ──────────────
function StepPlan({ plans, value, onChange }) {
  if (!plans.length) {
    return (
      <div>
        <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-3">
          Formulation plans
        </h3>
        <p className="text-sm text-[#556B57]">
          No plans configured for this product — please contact our team.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-2">
        Select formulation plan
      </h3>
      <p className="text-sm text-[#556B57] mb-6">
        Each plan defines how many active ingredients your custom formula can
        contain. Higher tiers = more premium claim ingredients.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {plans.map((pl) => (
          <button
            key={pl.name}
            onClick={() => onChange(pl.name)}
            data-testid={`select-plan-${pl.name.toLowerCase()}`}
            className={`text-left rounded-2xl p-6 border transition ${
              value === pl.name
                ? "bg-[#1D3A2F] text-[#FDFBF7] border-[#1D3A2F]"
                : "bg-[#F7F4EE] border-[#D7D4CF]/60 hover:border-[#1D3A2F]/50"
            }`}
          >
            <div className="text-[10px] tracking-[0.24em] uppercase opacity-70">
              Plan
            </div>
            <div className="font-editorial text-3xl mt-2">{pl.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-editorial text-4xl">
                ₹{pl.price.toLocaleString("en-IN")}
              </span>
              <span className="text-xs opacity-70">/ unit</span>
            </div>
            {pl.max_key_actives != null && (
              <div className="mt-4 text-xs opacity-80">
                Up to <b>{pl.max_key_actives}</b> key actives
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ────────────── STEP 3 · Size (SKU) ──────────────
function StepSize({ skus, value, onChange }) {
  if (!skus.length) {
    return (
      <div>
        <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-3">Size</h3>
        <p className="text-sm text-[#556B57]">
          No SKU sizes configured for this product.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-6">
        Pick fill size
      </h3>
      <div className="flex flex-wrap gap-2">
        {skus.map((s, i) => (
          <button
            key={i}
            onClick={() => onChange(i)}
            data-testid={`select-size-${(s.size || "").replace(/\s+/g, "").toLowerCase()}`}
            className={`px-6 py-3 rounded-full transition ${
              value === i
                ? "bg-[#1D3A2F] text-[#FDFBF7]"
                : "bg-[#F7F4EE] text-[#1D3A2F] border border-[#D7D4CF]"
            }`}
          >
            {s.size || "Size"}{" "}
            <span className="opacity-70 text-xs">
              ({s.containers.length} containers)
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ────────────── STEP 4 · Container ──────────────
function StepContainer({ containers, size, value, onChange }) {
  if (!containers.length) {
    return (
      <div>
        <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-3">
          Container
        </h3>
        <p className="text-sm text-[#556B57]">No containers for this size.</p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-2">
        Choose your packaging
      </h3>
      <p className="text-sm text-[#556B57] mb-6">
        {containers.length} container options for {size}. Prices are per unit
        — added on top of your formulation cost.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[560px] overflow-y-auto pr-2">
        {containers.map((c, i) => (
          <button
            key={i}
            onClick={() => onChange(i)}
            data-testid={`select-container-${i}`}
            className={`text-left rounded-2xl overflow-hidden border transition ${
              value === i
                ? "border-[#1D3A2F] ring-2 ring-[#1D3A2F]/40"
                : "border-[#D7D4CF]/60 hover:border-[#1D3A2F]/50"
            } bg-[#F7F4EE]`}
          >
            <div className="aspect-square bg-white p-3 flex items-center justify-center">
              {c.image ? (
                <img
                  src={c.image}
                  alt={`Container ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
            <div className="p-3 flex items-center justify-between">
              <div className="text-xs text-[#556B57] tracking-[0.14em] uppercase">
                Option {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-sm font-medium text-[#1D3A2F]">
                ₹{c.price}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ────────────── STEP 5 · Finish + Logo ──────────────
function StepFinish({ options, logoUrl, value, onChange, onLogo }) {
  return (
    <div>
      <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-6">
        Label finish &amp; logo
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            data-testid={`select-finish-${o.id}`}
            className={`rounded-2xl px-4 py-6 border text-center transition ${
              value === o.id
                ? "bg-[#1D3A2F] text-[#FDFBF7] border-[#1D3A2F]"
                : "bg-[#F7F4EE] text-[#1D3A2F] border-[#D7D4CF]/60 hover:border-[#1D3A2F]/50"
            }`}
          >
            <div className="font-editorial text-lg">{o.name}</div>
          </button>
        ))}
      </div>
      <div>
        <label className="eyebrow block mb-3">Logo URL (optional)</label>
        <input
          data-testid="logo-url-input"
          type="url"
          placeholder="https://cdn.yourbrand.com/logo.png"
          value={logoUrl}
          onChange={(e) => onLogo(e.target.value)}
          className="w-full h-12 px-5 rounded-full bg-[#F7F4EE] border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#1D3A2F]"
        />
      </div>
    </div>
  );
}

// ────────────── STEP 6 · Details + Qty ──────────────
function DetailField({ label, k, type = "text", required, value, onChange }) {
  return (
    <div>
      <label className="eyebrow block mb-2">
        {label} {required && <span className="text-[#556B57]">*</span>}
      </label>
      <input
        data-testid={`details-${k}`}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(k, e.target.value)}
        className="w-full h-12 px-5 rounded-full bg-[#F7F4EE] border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#1D3A2F]"
      />
    </div>
  );
}

function StepDetails({ config, onChange, unitPrice }) {
  return (
    <div>
      <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-6">
        Your details
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DetailField
          label="Full name"
          k="name"
          required
          value={config.name}
          onChange={onChange}
        />
        <DetailField
          label="Email"
          k="email"
          type="email"
          required
          value={config.email}
          onChange={onChange}
        />
        <DetailField
          label="Phone"
          k="phone"
          type="tel"
          required
          value={config.phone}
          onChange={onChange}
        />
        <DetailField
          label="Company / Brand"
          k="company"
          value={config.company}
          onChange={onChange}
        />
        <DetailField
          label="Country"
          k="country"
          value={config.country}
          onChange={onChange}
        />
        <div>
          <label className="eyebrow block mb-2">
            Quantity (samples) <span className="text-[#556B57]">*</span>
          </label>
          <div className="flex items-center gap-3 h-12 px-4 rounded-full bg-[#F7F4EE] border border-[#D7D4CF]">
            <button
              type="button"
              onClick={() =>
                onChange("quantity", Math.max(1, config.quantity - 1))
              }
              data-testid="qty-decrement"
              className="w-8 h-8 rounded-full bg-[#FDFBF7] text-[#1D3A2F] font-editorial text-xl"
            >
              −
            </button>
            <span
              className="flex-1 text-center text-lg text-[#1D3A2F]"
              data-testid="qty-value"
            >
              {config.quantity}
            </span>
            <button
              type="button"
              onClick={() => onChange("quantity", config.quantity + 1)}
              data-testid="qty-increment"
              className="w-8 h-8 rounded-full bg-[#1D3A2F] text-[#FDFBF7] font-editorial text-xl"
            >
              +
            </button>
          </div>
          {unitPrice > 0 && (
            <div className="text-xs text-[#556B57] mt-2 pl-5">
              {formatINR(unitPrice)} per sample (inc. GST)
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="eyebrow block mb-2">Additional notes</label>
          <textarea
            data-testid="details-notes"
            rows={3}
            value={config.notes}
            onChange={(e) => onChange("notes", e.target.value)}
            className="w-full px-5 py-3 rounded-2xl bg-[#F7F4EE] border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#1D3A2F] resize-none"
          />
        </div>
      </div>
    </div>
  );
}

// ────────────── STEP 7 · Review ──────────────
function ReviewRow({ l, v }) {
  return (
    <div className="flex justify-between border-b border-[#D7D4CF]/60 py-3">
      <span className="eyebrow">{l}</span>
      <span className="text-[#1D3A2F] font-medium text-right">{v || "—"}</span>
    </div>
  );
}

function StepReview({
  product,
  plan,
  sku,
  container,
  config,
  opts,
  unitPrice,
  total,
  gstRate,
}) {
  return (
    <div>
      <h3 className="font-editorial text-3xl text-[#1D3A2F] mb-6">
        Review your sample
      </h3>
      <div className="space-y-1">
        <ReviewRow l="Product" v={product?.name} />
        <ReviewRow
          l="Formulation Plan"
          v={plan ? `${plan.name} · ₹${plan.price}/unit` : "—"}
        />
        <ReviewRow l="Size" v={sku?.size} />
        <ReviewRow
          l="Container"
          v={container ? `₹${container.price}/unit` : "—"}
        />
        <ReviewRow l="Quantity" v={config.quantity} />
        <ReviewRow l="Name" v={config.name} />
        <ReviewRow l="Email" v={config.email} />
        <ReviewRow l="Phone" v={config.phone} />
        <ReviewRow l="Country" v={config.country} />
      </div>
      <div className="mt-8 rounded-2xl bg-[#F7F4EE] border border-[#D7D4CF] p-6 space-y-2">
        <div className="flex justify-between text-sm text-[#222222]/80">
          <span>Per-sample cost (inc. GST @ {gstRate}%)</span>
          <span className="font-medium">{formatINR(unitPrice)}</span>
        </div>
        <div className="flex items-end justify-between pt-2 border-t border-[#D7D4CF]/60">
          <div>
            <div className="eyebrow mb-1">Order total</div>
            <div
              className="font-editorial text-4xl text-[#1D3A2F]"
              data-testid="review-total"
            >
              {formatINR(total)}
            </div>
          </div>
          <div className="text-xs tracking-[0.16em] uppercase text-[#556B57] text-right">
            Delivery
            <br />
            <span className="text-[#1D3A2F] font-medium">72 hours</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}
